import React, { useEffect, useRef } from 'react'
import * as R from 'ramda'
import { Grid, Image, Segment } from 'semantic-ui-react'
import { useLocation } from 'react-router-dom'

import Title from 'shared/components/Title'
import CyclrIntegrationAuthenticationSegment from 'components/CyclrIntegrationAuthenticationSegment'
import TemplateAccordion from './TemplateAccordion'

export type IntegrationTemplate = {
  template_id: string
  name: string
  description: string
  image: string
  template_text_link: string
  template_link: string
  is_active: boolean
  cycle_id: null | string
  options: string[]
  order: number
  integration_level: 'survey_integration' | 'ticket_integration' | 'account_wide_integration'
}

type Props = {
  connectorId: string
  connectorName: string
  connectorDescription: string
  connectorImage: string
  isAuthenticated: boolean
  accountWideIntegrationTemplates: IntegrationTemplate[]
  surveyIntegrationTemplates: IntegrationTemplate[]
  ticketIntegrationTemplates: IntegrationTemplate[]
  activateIntegration: () => void
  deactivateIntegration: () => void
  deleteIntegration: () => void
  authenicateConnector: () => void
  revokeAccess: () => void
  authenticatioType: null | 'basic' | 'oauth2' | 'api_key'
  authenticationSettings: []
  connectorProperties: []
  isActivating: boolean
  isAuthenticateSuccessful: boolean
  isAuthenticating: boolean
  oauthRedirectUrl: string | null
  isRevoking: boolean
}

export default function EmbeddingCyclrIntegration({
  connectorId,
  connectorName,
  connectorDescription,
  connectorImage,
  isAuthenticated,
  accountWideIntegrationTemplates,
  surveyIntegrationTemplates,
  ticketIntegrationTemplates,
  activateIntegration,
  deactivateIntegration,
  deleteIntegration,
  authenicateConnector,
  revokeAccess,
  authenticatioType,
  authenticationSettings,
  connectorProperties,
  isActivating,
  isAuthenticateSuccessful,
  isAuthenticating,
  oauthRedirectUrl,
  isRevoking,
}: Props) {
  const ticketIntegrationSectionRef = useRef(null)
  const location = useLocation()

  useEffect(() => {
    const hasTicketIntegrationHash = location.hash === '#ticket-integrations'
    if (R.prop('current', ticketIntegrationSectionRef) && hasTicketIntegrationHash) {
      ticketIntegrationSectionRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
  }, [location.hash])

  return (
    <>
      <Title title={`${connectorName}`} />
      <Grid stackable>
        <Grid.Row>
          <Grid.Column width={2}>
            <Segment className="flex justify-center">
              <Image src={connectorImage} className="max-h-28" />
            </Segment>
          </Grid.Column>
          <Grid.Column width={14} className="md:!pl-4">
            <h2>{connectorName}</h2>
            <div dangerouslySetInnerHTML={{ __html: connectorDescription }} />
          </Grid.Column>
        </Grid.Row>
      </Grid>
      <div className="!my-4">
        <CyclrIntegrationAuthenticationSegment
          isAuthenticated={isAuthenticated}
          name={connectorName}
          onActivate={authenicateConnector}
          onDeactivate={revokeAccess}
          loading={isAuthenticating || isRevoking}
          authenticatioType={authenticatioType}
          authenticationSettings={authenticationSettings}
          connectorProperties={connectorProperties}
          isAuthenticateSuccessful={isAuthenticateSuccessful}
          oauthRedirectUrl={oauthRedirectUrl}
        />
      </div>
      <h3>Configure integrations</h3>
      {accountWideIntegrationTemplates.length > 0 && (
        <TemplateAccordion
          title="Account-wide integrations"
          connectorId={connectorId}
          connectorName={connectorName}
          isAuthenticated={isAuthenticated}
          integrationTemplates={accountWideIntegrationTemplates}
          activateIntegration={activateIntegration}
          deactivateIntegration={deactivateIntegration}
          deleteIntegration={deleteIntegration}
          isActivating={isActivating}
        />
      )}

      {surveyIntegrationTemplates.length > 0 && (
        <TemplateAccordion
          title="Survey integrations"
          connectorId={connectorId}
          connectorName={connectorName}
          isAuthenticated={isAuthenticated}
          integrationTemplates={surveyIntegrationTemplates}
          activateIntegration={activateIntegration}
          deactivateIntegration={deactivateIntegration}
          deleteIntegration={deleteIntegration}
          isActivating={isActivating}
        />
      )}

      {ticketIntegrationTemplates.length > 0 && (
        <div ref={ticketIntegrationSectionRef} className="mt-6">
          <TemplateAccordion
            title="Ticket integrations"
            connectorId={connectorId}
            connectorName={connectorName}
            isAuthenticated={isAuthenticated}
            integrationTemplates={ticketIntegrationTemplates}
            activateIntegration={activateIntegration}
            deactivateIntegration={deactivateIntegration}
            deleteIntegration={deleteIntegration}
            isActivating={isActivating}
          />
        </div>
      )}
    </>
  )
}



// WEBPACK FOOTER //
// ./src/components/EmbeddingCyclrIntegration/index.tsx